import { Injectable } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { faGraduationCap, faUniversity, faAward, faFileInvoice, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, combineLatest, forkJoin, map, mergeMap, Observable, of, tap, throwError } from "rxjs";
import { Step, StepState } from "./form-step.type";
import  * as _  from 'lodash-es';
import { Institution } from "@gwc/datasources/gwc-api/types/institution.types";
import { GwcApiInstitutionService } from "@gwc/datasources/gwc-api/institution.service";
import { MatDialog } from "@angular/material/dialog";
import { ProcessOrderDialogComponent } from "./dialog/process-order-dialog/process-order-dialog.component";
import { StripeConnectService } from "@gwc/services/stripe-connect.service";
import { GwcApiCasualOrdersService } from "@gwc/datasources/gwc-api/casual-orders.service";
import { GwcApiOrdersService } from "@gwc/datasources/gwc-api/orders.service";
import { catchError } from 'rxjs/operators';
import { CasualHirePricingResolution, GwcApiCasualHirePricingService } from "@gwc/datasources/gwc-api/casual-hire-pricing.service";
import { DateHelperService } from "@f3/services/date-helper.service";


export interface OrderData {
    institution: null;
    qualifications: any[];
    collection: null;
    regalia: any[];
    details_payment: null;
    stripe: null;
}


@Injectable({
    providedIn: 'root'
})
export class CasualHireFormService {

    // @Public Properties
    public stripeFields!: () => { stripeCardNumber: any, stripeCardExpiry: any, stripeCardCvc: any };


    // @Private Properties
    private _theSteps: Step[] = [
        {
            id: 'institution',
            slug: 'select-institution',
            displayTitle: 'Select Institution',
            order: 1,
            icon: faUniversity,
            includeOrderSummary: false,
            state: StepState.UNCOMPLETED
        },
        {
            id: 'qualifications',
            slug: 'select-qualifications',
            displayTitle: 'Select Qualifications',
            order: 2,
            icon: faAward,
            includeOrderSummary: true,
            state: StepState.UNCOMPLETED
        },
        {
			id: 'collection',
			slug: 'select-collection-date',
			displayTitle: 'Collection Date',
			order: 3,
			icon: faHandshake,
			includeOrderSummary: true,
            state: StepState.UNCOMPLETED
		},
		{
			id: 'regalia',
			slug: 'select-regalia',
			displayTitle: 'Select Your Regalia',
			order: 4,
			icon: faGraduationCap,
			includeOrderSummary: true,
            state: StepState.UNCOMPLETED

		},
		{
			id: 'details',
			slug: 'details-payment',
			displayTitle: 'Details & Payment',
			order: 5,
			icon: faFileInvoice,
			includeOrderSummary: true,
            state: StepState.UNCOMPLETED
		}
    ];  

    private _steps: BehaviorSubject<Step[]> = new BehaviorSubject<Step[]>([]);

    private _currentStepValid = new BehaviorSubject<boolean>(false)

    private _institutions = new BehaviorSubject<Institution[]>([]);

    private _orderData = new BehaviorSubject<any>({
        institution: null,
        qualifications: [],
        collection: null,
        regalia: [],
        details_payment: {},
        stripe: {}
    });

    private _lastPricingRequestId = 0;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _matDialog: MatDialog,
        private _gwcApiInstitutionService: GwcApiInstitutionService,
        private _dateHelper: DateHelperService,
        private _stripeConnectService: StripeConnectService,
        private _gwcApiCasualOrdersService: GwcApiCasualOrdersService,
        private _gwcApiOrdersService: GwcApiOrdersService,
        private _casualHirePricingService: GwcApiCasualHirePricingService
    ) { 

        const storedOrderData = this.retrieveOrderData();
        if(storedOrderData) {
            this._orderData.next(storedOrderData);
            this._refreshPricingForCurrentOrder(storedOrderData);
        } else {
            

        }

        // Initialize step state based on current URL (handles hot reload)
        this._updateStepStateFromUrl(this._router.url);

        this._router.events.subscribe(
			(event) => {
				if(event instanceof NavigationEnd) {
					const url = event.urlAfterRedirects.substr(0,1) == '/' ? event.urlAfterRedirects.substring(1) : event.url;
                    this._updateStepStateFromUrl(url);
                }
			}
		)

    }

    /**
     * Update step states based on the current URL
     */
    private _updateStepStateFromUrl(url: string) {
        const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
        const parsedUrl = cleanUrl.split('/');

        if(_.indexOf(parsedUrl, 'intro') > -1) {
            // Reset all steps to uncompleted for intro page
            this._theSteps.forEach(step => step.state = StepState.UNCOMPLETED);
            this._steps.next(this._orderedSteps());
            return;
        }

        // Find the index in the steps of this slug
        let foundCurrent = false;

        this._orderedSteps().map(step => {
            if(!foundCurrent) {
                if(_.indexOf(parsedUrl, step.slug) > -1) {
                    foundCurrent = true;
                    step.state = StepState.INPROGRESS;
                } else {
                    step.state = StepState.COMPLETED;
                }
            } else {
                step.state = StepState.UNCOMPLETED;
            }
        });

        this._steps.next(this._orderedSteps());
    }


    //--------------------------------------------------------------------------------
    // @Accessors
    //--------------------------------------------------------------------------------
    
    get steps$(): Observable<Step[]> {
        return this._steps.asObservable()
    }

    get currentStepValid$(): Observable<boolean> {
        return this._currentStepValid.asObservable()
    }

    get institutions$(): Observable<Institution[]> {
        return this._institutions.asObservable()
    }

    get orderData$(): Observable<any> {
        return this._orderData.asObservable()
    }


    //--------------------------------------------------------------------------------
    // @Public Methods
    //--------------------------------------------------------------------------------

    public addToOrderData(key: string, value: any, stepValid: boolean) {
        const current = _.cloneDeep(this._orderData.value);
        current[key] = value;

        const finalizeUpdate = (orderData: any) => {
            const persisted = this._persistOrderDataState(orderData);
            const gownAndHeadValid = this._gownAndHeadCircumferenceValid(persisted);
            this.updateStepValidity(stepValid && gownAndHeadValid);
        };

        const requiresPricingRefresh = ['regalia', 'collection', 'institution'].includes(key);

        if (requiresPricingRefresh) {
            const pricingRequestId = ++this._lastPricingRequestId;

            this._resolveRegaliaPricing(current).subscribe({
                next: (regalia) => {
                    if (pricingRequestId !== this._lastPricingRequestId) {
                        return;
                    }

                    current.regalia = regalia;
                    finalizeUpdate(current);
                },
                error: (error) => {
                    if (pricingRequestId !== this._lastPricingRequestId) {
                        return;
                    }

                    console.error('Failed to resolve casual hire pricing', { error, key });
                    finalizeUpdate(current);
                }
            });

            return;
        }

        finalizeUpdate(current);
    }

    /**
     * 
     */
    public updateStepValidity(value: boolean) {
        this._currentStepValid.next(value)
    }

    /**
     * Get Institutions
     */
    public getInstitutions(): Observable<Institution[]> {
        return this._gwcApiInstitutionService.getCasualHireInstitutions().pipe(
            tap((institutions) => {
                this._institutions.next(institutions)
            })
        )
    }

    /**
     * Get teh current step
     */
    public getCurrentStep() {
        return this._currentStep()
    }

    /**
     * Get the  next step
     */
    public getNextStep() {
        

        return this._orderedSteps()[this._getCurrentStepIndex() + 1];
    }

    /**
     * go to the nezt step 
     */
    public goToNextStep(activatedRoute: ActivatedRoute) {
		//check whether is the last item in the step
		// this._logger.info('Step Completed', { order_code: this._orderData.order_code, step_completed: this._getCurrentStepKey() })


		if(this._isLastStep()) {
			//opening model window and start to process the order

			const dialogRef = this._matDialog.open(ProcessOrderDialogComponent, {
				disableClose: true,
				panelClass: ['w-1/2', 'mobile:w-full', 'mobile:mx-8', 'py-12']
			})

			dialogRef.afterClosed().subscribe(result => {
				this._router.navigate(['order-confirmation'], { relativeTo: activatedRoute });
			})

		} else {
            this._router.navigate([this._orderedSteps()[this._getCurrentStepIndex() + 1].slug], { relativeTo: activatedRoute })
		}

	}

	public goToPrevStep() {
		// this._router.navigate([this.OrderedSteps[this.OrderedSteps.findIndex(step => step.id === this._getCurrentStepKey()) - 1].slug], { relativeTo: this._activatedRoute})
	}


    //--------------------------------------------------------------------------------
    // @Private Methods
    //--------------------------------------------------------------------------------

    /**
     * Stopre the order data in local storage
     */
    public storeOrderData(orderData: OrderData) {
        const epoch = this._dateHelper.getUnixTimestamp();
        const persistedOrderData = this._toPersistableOrderData(orderData);
        _.assign(persistedOrderData, { 'lastEdit': epoch })

        localStorage.setItem('orderData', JSON.stringify(persistedOrderData))
    }

    /**
     * remove the order data from local storage
     */
    public deleteOrderData() {
       
        this._orderData.next({
            institution: null,
            qualifications: [],
            collection: null,
            regalia: [],
            details_payment: {},
            stripe: {}
        })

        this._lastPricingRequestId++;

        localStorage.removeItem('orderData');
        localStorage.removeItem('qualification_image');
    }


    public retrieveOrderData() {
        const data = localStorage.getItem('orderData')

        if(!data) return null;

        const pData = JSON.parse(data);

        const diff = this._dateHelper.getTimeDiffMinutes(pData.lastEdit)

        if( diff > 5) {
            this.deleteOrderData();

            this._router.navigate(['/']);
            
            return null;

        } else {
            return pData;
        }

    }

    public stripeFieldsFeed(stripeElements: any) {
		this.stripeFields = stripeElements
	}

    /**
     * Process the order
     */    
     public processOrder() {


		let stripeParams: any;
		let tokenId: string;

		let casual_order_id: string | null = null;

        const orderData = this._orderData.value

		// this._logger.info('Process Order Started', {
		// 	order_code: this._orderData.order_code
		// })

		return new Observable((subscriber: any) => {

			const stripeElements = this.stripeFields();

			this._stripeConnectService.createToken(stripeElements.stripeCardNumber, {name: orderData.details_payment?.creditCard.name}).pipe(
				mergeMap((result: any) => {

            		console.log('OrderProcess - CreateToken', { result: result })

					if(result.error) {
						return throwError(() => {
							    message: 'ERROR_STRIPE_TOKEN'
						    })
					}

					subscriber.next('SUCCESS_STRIPE_TOKEN')

					// const payload = JSON.parse(this._cookieService.get('gwc-order')) 

                    //construct the payload
                    const payload = {
                        institution: {
                            institution_id: orderData.institution.institution_id
                        },
                        qualifications: orderData.qualifications.map((qual: any) => qual.institution_qualification_id),
                        collection: orderData.collection,
                        regalia: orderData.regalia,
                        details_payment: {
                            details: orderData.details_payment.details,
                            address: orderData.details_payment.address,
                            agreements: orderData.details_payment.agreements   
                        }
                    }

                    const total = this._calculateOrderTotal(orderData.regalia) * 100;

                    // Handle both result.token.id and result.id (depending on Stripe response structure)
                    tokenId = result.token?.id || result.id;
                    console.log('OrderProcess - TokenId extracted', { tokenId, resultToken: result.token, resultId: result.id });
                    stripeParams = {stripe: {token_id: tokenId, amount: total }};
                    console.log('OrderProcess - StripeParams created', { stripeParams });
                    _.assign(payload, stripeParams)

					return this._gwcApiCasualOrdersService.postCasualOrder(payload);
				}),
                mergeMap((orderResult: any) => {

            		console.log('OrderProcess - PostOrders', { result: orderResult })

                    //upload the image

                    let secondCall = of({})
                    if(window.localStorage.getItem('qualification_image')) {
                        const f = JSON.parse(window.localStorage.getItem('qualification_image') as string)

                        console.log('OrderProcess - File Details', { file: f.file })

                        const payload = {
                            'content-type': f.file.type,
                            'image': f.content.split(',')[1]
                        }

                        secondCall = this._gwcApiOrdersService.uploadQualificationImage(orderResult.order_id, payload )
                    }

                    return combineLatest([
                        of(orderResult),
                        secondCall
                    ])

                }),
				mergeMap(([result, fileResult]) => {

            		console.log('OrderProcess - Image', { result: result, file: fileResult })

					if( result.error) {
						return throwError(() => {message: 'ERROR_CREATE_ORDER'});
					}

					if ((fileResult as any).error) {
						return throwError(() => ({message: 'ERROR_UPLOAD_QUALIFICATION_IMAGE'}));
					}

					casual_order_id = result.casual_order_id;

					subscriber.next('SUCCESS_CREATE_ORDER')

                    console.log('OrderProcess - StartProcessPayment', { casual_order_id: casual_order_id, stripe: stripeParams})

                    return this._gwcApiCasualOrdersService.postProcessPayment(<string>casual_order_id, stripeParams)
				}),
				mergeMap((result: any) => {

            		console.log('OrderProcess - PostProcessPayment', { result: result })


					if(result.error) {
						return throwError(() => {message: 'ERROR_PROCESS_PAYMENT'});
					}

					subscriber.next('SUCCESS_PROCESS_PAYMENT')

					return this._gwcApiCasualOrdersService.postConfirmOrder(<string>casual_order_id);
				}),
				map((result: any) => {
					if(result.error) {
						return throwError(() => {message: 'ERROR_CONFIRM_ORDER'});
					}

					console.log('Process Order Completed', {
						order_code: orderData?.order_code || ''
					})

					subscriber.next('SUCCESS_CONFIRM_ORDER');
					subscriber.next('OK');

					return
				})

			).subscribe({
                next: (result: any) => {
                    console.log('OrderFinished')
                },
                error: (err: any) => console.error('error', { error: err })
            })

		})

	}

    public getSystemSettings(): Observable<string[]> {
        return this._gwcApiOrdersService.getSystemSettings().pipe(
          map((data: any) => data[0].value)
        );
      }

    //--------------------------------------------------------------------------------
    // @Private Methods
    //--------------------------------------------------------------------------------

    private _orderedSteps() {
        return this._theSteps.sort((a, b) => a.order - b.order)
    }

    private _currentStep() {
        return this._theSteps.find(step => step.state === StepState.INPROGRESS)
    }

    private _getCurrentStepIndex() {
        return this._orderedSteps().findIndex(step => step.state === StepState.INPROGRESS)
    }

    private _getCurrentStepKey() {
        return this._orderedSteps().find(step => step.state === StepState.INPROGRESS)?.id || null
	}

    private _isLastStep() {
		return this._orderedSteps().findIndex(step => step.id == this._getCurrentStepKey()) >= this._orderedSteps().length - 1
	}

    private _gownAndHeadCircumferenceValid(orderData: any) {
        for (const item of orderData.regalia) {
            // Determine if the item requires gownHeight or headCircumference
            const requiresGownHeight = item.regalia?.list_regalia_cat.list_regalia_cat_id === '8d30a040-afeb-42f8-bd3c-cd20c34ced7f'
            const requiresHeadCircumference = item?.regalia?.list_regalia_cat.list_regalia_cat_id === 'cd068b87-972c-4d22-8a1c-693fe8c0d4e7'
            
            // Check for gownHeight if required
            if (requiresGownHeight && (!item.data?.gownHeight || item.data?.gownHeight <= 0)) {
                return false; // gownHeight is required but not valid
            }
            
            // Check for headCircumference if required
            // if (requiresHeadCircumference && (!item.data?.headCircumference || item.data?.headCircumference <= 0)) {
            //     return false; // headCircumference is required but not valid
            // }
        }
        // All items passed the checks
        return true;
    }

    private _persistOrderDataState(orderData: any) {
        const persistedOrderData = this._toPersistableOrderData(orderData);
        this.storeOrderData(persistedOrderData);
        this._orderData.next(persistedOrderData);
        return persistedOrderData;
    }

    private _toPersistableOrderData(orderData: any): any {
        const persistedOrderData = _.cloneDeep(orderData);
        const qualImage = persistedOrderData?.details_payment?.qualification?.qualImage;

        if (qualImage) {
            persistedOrderData.details_payment.qualification.qualImage = {
                file: qualImage.file ?? null,
                stored: true
            };
        }

        return persistedOrderData;
    }

    private _resolveRegaliaPricing(orderData: any): Observable<any[]> {
        const regalia = orderData?.regalia || [];

        if (!regalia.length) {
            return of([]);
        }

        const institutionId = orderData?.institution?.institution_id ?? null;
        const effectiveOn = orderData?.collection?.pickup ?? null;

        const currencyFallback = regalia?.[0]?.currency || 'NZD';

        const requests: Observable<any>[] = regalia.map((item: any) => {
            const basePrice = item?.base_price ?? item?.price ?? 0;
            const numericBasePrice = Number(basePrice ?? 0);

            if (!this._requiresPricingLookup(item)) {
                return of(this._hydrateRegaliaPricing(item, null, numericBasePrice, currencyFallback, false));
            }

            return this._casualHirePricingService.resolve({
                regalia_id: item.regalia_id,
                base_price: numericBasePrice,
                institution_id: institutionId,
                effective_on: effectiveOn,
                list_regalia_cat_id: item?.regalia?.list_regalia_cat_id ?? null,
                list_regalia_sub_cat_id: item?.regalia?.list_regalia_sub_cat_id ?? null
            }).pipe(
                map((resolution) => this._hydrateRegaliaPricing(item, resolution, numericBasePrice, currencyFallback, false)),
                catchError((error) => {
                    console.error('Casual hire pricing resolution failed', { error, regalia_id: item?.regalia_id });
                    return of(this._hydrateRegaliaPricing(item, null, numericBasePrice, currencyFallback, true));
                })
            );
        });

        return forkJoin(requests);
    }

    private _hydrateRegaliaPricing(
        item: any,
        resolution: CasualHirePricingResolution | null,
        basePrice: number,
        currencyFallback: string,
        hasError: boolean
    ) {
        const normalizedBasePrice = Number(basePrice ?? 0);
        const finalPrice = this._resolveFinalPriceValue(normalizedBasePrice, resolution);
        const currency = resolution?.currency || item?.currency || currencyFallback || 'NZD';
        const modifierAmount = resolution?.modifier_amount !== undefined && resolution?.modifier_amount !== null
            ? Number(resolution.modifier_amount)
            : 0;

        return {
            ...item,
            base_price: normalizedBasePrice,
            price: finalPrice,
            currency,
            modifier_amount: modifierAmount,
            modifier: resolution?.modifier ?? item?.modifier ?? null,
            pricing_resolution: resolution,
            pricing_error: hasError
        };
    }

    private _requiresPricingLookup(item: any): boolean {
        if (!item) {
            return false;
        }

        const specialIds = ['late_fee', 'credit'];

        if (specialIds.includes(item.institution_qualification_regalia_id)) {
            return false;
        }

        return Boolean(item.regalia_id);
    }

    private _calculateOrderTotal(regalia: any[]): number {
        if (!regalia?.length) {
            return 0;
        }

        return _.sumBy(regalia, (item: any) => {
            const basePrice = Number(item?.base_price ?? item?.price ?? 0);
            return this._resolveFinalPriceValue(basePrice, item?.pricing_resolution);
        });
    }

    private _resolveFinalPriceValue(basePrice: number, resolution?: CasualHirePricingResolution | null): number {
        if (resolution && resolution.final_price !== null && resolution.final_price !== undefined) {
            return Number(resolution.final_price);
        }

        if (basePrice === null || basePrice === undefined || Number.isNaN(basePrice)) {
            return 0;
        }

        return basePrice;
    }

    private _refreshPricingForCurrentOrder(orderData: any): void {
        if (!orderData?.regalia?.length) {
            return;
        }

        const snapshot = _.cloneDeep(orderData);

        const pricingRequestId = ++this._lastPricingRequestId;

        this._resolveRegaliaPricing(snapshot).subscribe({
            next: (regalia) => {
                if (pricingRequestId !== this._lastPricingRequestId) {
                    return;
                }

                snapshot.regalia = regalia;
                this._persistOrderDataState(snapshot);
            },
            error: (error) => {
                if (pricingRequestId !== this._lastPricingRequestId) {
                    return;
                }

                console.error('Failed to refresh pricing from stored order', { error });
                this._persistOrderDataState(snapshot);
            }
        });
    }

}
